import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "../../../../Utils/axiosInstance";
import toast from "react-hot-toast";
import { useLockBodyScroll } from "../../../Hooks/useLockBodyScroll";

const OTPModal = ({ show, onClose, onVerified, identifier }) => {
  const [animate, setAnimate] = useState(false);
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [timer, setTimer] = useState(30);
  const otpSentRef = useRef(false);

  useLockBodyScroll(show);

  useEffect(() => {
    if (!show) {
      setOtp("");
      setTimer(30);
      otpSentRef.current = false;   // ← ref reset instead of state
      return;
    }
    setTimeout(() => {
      setAnimate(true);
    }, 10);
  }, [show]);

  useEffect(() => {
    if (show && !otpSentRef.current) {
      otpSentRef.current = true;   // synchronous — no re-render, no double-fire
      sendOTP();
    }
  }, [show]);   // ← dependency array simplified too; otpSentRef is stable

  useEffect(() => {

    let interval;

    if (timer > 0) {

      interval = setInterval(() => {

        setTimer((prev) => prev - 1);

      }, 1000);
    }

    return () => clearInterval(interval);

  }, [timer]);

  const sendOTP = async () => {

    try {

      setSending(true);

      await axios.post("/otp/send-otp", {
        identifier,
      });

      toast.success("OTP sent successfully");

      // Restart timer
      setTimer(30);

    } catch (err) {

      toast.error(
        err?.response?.data?.message || "Failed to send OTP"
      );

    } finally {

      setSending(false);

    }
  };

  const handleVerify = async () => {
    if (!otp) return toast.error("Enter OTP");

    try {
      setLoading(true);
      const res = await axios.post("/otp/verify-otp", {
        identifier,
        otp,
      });

      if (res.data.success) {
        toast.success("OTP verified");
        onVerified();
        handleClose();
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Invalid OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setAnimate(false);
    setTimeout(onClose, 250);
  };

  if (!show) return null;

  return createPortal(
    <div
      className={`fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs transition-opacity duration-200 ${animate ? "opacity-100" : "opacity-0"}`}
    >
      <div
        className={`bg-white w-full max-w-md rounded-3xl p-6 shadow-2xl transition-all duration-200 ${animate ? "scale-100 translate-y-0" : "scale-95 translate-y-4"}`}
      >
        <h2 className="text-center font-bold text-lg text-primaryColor mb-4">
          OTP Verification
        </h2>

        <p className="text-xs text-gray-500 text-center mb-4">
          Enter OTP sent to {identifier}
        </p>

        <input
          value={otp}
          onChange={(e) => {
            let value = e.target.value;

            value = value.replace(/\D/g, "");

            if (value.length > 6) value = value.slice(0, 6);

            setOtp(value);
          }}
          inputMode="numeric" // ✅ mobile numeric keyboard
          pattern="[0-9]*" // ✅ extra restriction
          maxLength={6} // ✅ browser-level safety
          placeholder="Enter 6 digit OTP"
          className="w-full border rounded-lg px-4 py-3 text-center text-lg tracking-widest outline-none focus:border-primaryColor"
        />

        <button
          onClick={handleVerify}
          disabled={loading || otp.length !== 6}
          className="w-full mt-4 bg-primaryColor text-white py-3 rounded-xl font-semibold disabled:opacity-50"
        >
          {loading ? "Verifying..." : "Verify OTP"}
        </button>

        <button
          onClick={sendOTP}
          disabled={sending || timer > 0}
          className="w-full mt-2 text-sm font-medium disabled:text-gray-400 text-primaryColor"
        >
          {sending
            ? "Sending OTP..."
            : timer > 0
              ? `Resend OTP in ${timer}s`
              : "Resend OTP"}
        </button>

        <button
          onClick={handleClose}
          className="w-full mt-2 border py-3 rounded-xl"
        >
          Cancel
        </button>
      </div>
    </div>,
    document.body,
  );
};

export default OTPModal;
